/**
 * InterfacrContext.tsx - React Context for Interfacr
 * 
 * CHANGE HISTORY:
 * - 251026_0755 danielbeer: Initial creation - React wrapper for InterfacrManager
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { InterfacrManager, InterfaceCommand, InterfaceResult, InterfaceEvent, InterfacrConfig } from './InterfacrManager';

interface InterfacrContextValue {
  interfacr: InterfacrManager;
  execute: (command: InterfaceCommand) => Promise<InterfaceResult>;
  stats: ReturnType<typeof InterfacrManager.prototype.getStats>;
  events: InterfaceEvent[];
}

const InterfacrContext = createContext<InterfacrContextValue | null>(null);

interface InterfacrProviderProps {
  children: React.ReactNode;
  config?: InterfacrConfig;
  interfacr?: InterfacrManager;
}

/**
 * InterfacrProvider - Provides Interfacr context only
 * 
 * This provider ONLY provides the InterfacrManager context.
 * It does NOT wrap any other providers.
 * 
 * Apps must set up their own provider hierarchy:
 * 1. ActrProvider (foundation)
 * 2. Actr-dependent providers (CommandrProvider, NotifyrProvider, SkinnrProvider, etc.)
 * 3. InterfacrProvider (this)
 * 4. Other providers as needed
 * 
 * See PROVIDER_DEPENDENCY_ANALYSIS.md for details.
 */
export function InterfacrProvider({ 
  children, 
  config, 
  interfacr: providedInterfacr
}: InterfacrProviderProps) {
  const [interfacr] = useState(() => providedInterfacr || new InterfacrManager(config));
  const [stats, setStats] = useState(interfacr.getStats());
  const [events, setEvents] = useState<InterfaceEvent[]>([]);

  useEffect(() => {
    // Subscribe to events
    const unsubscribe = interfacr.on((event) => {
      setEvents((prev) => [...prev.slice(-99), event]); // Keep last 100 events
      setStats(interfacr.getStats());
    });

    return unsubscribe;
  }, [interfacr]);

  const execute = useCallback(
    async (command: InterfaceCommand) => {
      return interfacr.execute(command);
    },
    [interfacr]
  );

  return (
    <InterfacrContext.Provider value={{ interfacr, execute, stats, events }}>
      {children}
    </InterfacrContext.Provider>
  );
}

/**
 * Hook to access Interfacr context
 */
export function useInterfacr() {
  const context = useContext(InterfacrContext);
  if (!context) {
    throw new Error('useInterfacr must be used within InterfacrProvider');
  }
  return context;
}

/**
 * Hook to execute interface commands
 */
export function useInterfaceCommand() {
  const { execute } = useInterfacr();
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<InterfaceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const executeCommand = useCallback(
    async (command: InterfaceCommand) => {
      setIsExecuting(true);
      setError(null);
      
      try {
        const result = await execute(command);
        setResult(result);
        
        if (!result.success) {
          setError(result.error || 'Command failed');
        }
        
        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      } finally {
        setIsExecuting(false);
      }
    },
    [execute]
  );

  return {
    execute: executeCommand,
    isExecuting,
    result,
    error,
  };
}

/**
 * Hook to access interface statistics
 */
export function useInterfaceStats() {
  const { stats } = useInterfacr();
  return stats;
}

/**
 * Hook to access interface events
 */
export function useInterfaceEvents(limit = 100) {
  const { events } = useInterfacr();
  return events.slice(-limit);
}
